import { useState } from "react";
import MarkdownComponents from "../../utils/MarkdownComponents";

const markdown = `
# Create New Contact

To add a new contact, fill out the form and click **Create Contact**.

## Contact Creation Form

\`\`\`
+-------------------------------+
|      New Contact Form         |
+-------------------------------+
| Name:         [___________]   |
| Email:        [___________]   |
| Mobile:       [___________]   |
| Organisation: [___ID___]      |
| Division:     [___ID___]      |
| Department:   [___ID___]      |
| Job Title:    [___________]   |
| Known As:     [___________]   |
| Office Loc:   [___________]   |
| Password:     [___________]   |
| Is Active:    [x]             |
|      [Create Contact]         |
+-------------------------------+
\`\`\`

**Required fields:**  
- Name, Email, Organisation ID, Division ID, Department ID, Job Title, Mobile, Known As, Office Location, Password

---

## What Happens After Submit?

1. The user fills the form and clicks **Create Contact**.
2. The frontend sends a POST request to the backend:

    \`\`\`http
    POST https://simplisysapi.vizionsys.com/gateway/Contacts
    Content-Type: application/json

    {
      "name": "name new",
      "email": "ex@ex.com",
      "contactOrganisationId": 30,
      "divisionId": 3,
      "departmentId": 1,
      "jobTitle": "jon eco",
      "mobilePhone": "+44 2323 23232",
      "knownAs": "newcont",
      "officeLocation": "odflokc",
      "password": { "passwordHash": "tempass" },
      "isActive": true,
      ... (other fields)
    }
    \`\`\`

3. If successful, the backend responds with the new contact data (including generated IDs).

    \`\`\`json
    {
      "id": 258097,
      "name": "name new",
      "userName": "name1039",
      "email": "ex@ex.com",
      ...
    }
    \`\`\`

---

## Security Note

- Never share passwords in plain text.
- Always use HTTPS.
- Only authorized users should create contacts.
`;

const API_URL = "https://simplisysapi.vizionsys.com/gateway/Contacts";
const API_HEADERS = { "Content-Type": "application/json" };

const ANIMATION_STEPS = [
  { label: "Sending contact creation request to API...", key: "request" },
  { label: "Received new contact data from server!", key: "response" },
  { label: "Contact creation complete! 🎉", key: "done" },
];

const defaultForm = {
  isVIP: false,
  name: "name new",
  userName: "",
  contactOrganisationId: 30,
  baseLocationId: 2,
  divisionId: 3,
  departmentId: 1,
  teamsId: "",
  contactTypeId: 1,
  email: "ex@ex.com",
  managerId: 257998,
  jobTitle: "jon eco",
  mobilePhone: "+44 2323 23232",
  selfServiceRoleId: 0,
  isSelfServiceAccess: true,
  isSingleSignOnEnabled: false,
  alert: "",
  businessPhone: "",
  whatsAppNumber: "+44",
  knownAs: "newcont",
  officeLocation: "odflokc",
  profilePictureId: 0,
  employeeHireDate: "2025-07-10T18:30:00.000Z",
  externalEmployeeId: "",
  ageGroup: "",
  consentProvidedForMinor: "",
  legalAgeGroupClassification: "",
  isActive: true,
  roleId: 5,
  externalId: "  ",
  password: { passwordHash: "tempass" },
};

export default function ContactCreationPage() {
  const [form, setForm] = useState(defaultForm);
  const [apiData, setApiData] = useState(null);
  const [animationStep, setAnimationStep] = useState(-1);
  const [copyMsg, setCopyMsg] = useState("");

  // Simulate backend contact creation
  const fakeCreateContact = (payload) =>
    new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            ...payload,
            id: 258097,
            userName: "name1039",
            password: {
              id: 821,
              passwordHash: payload.password.passwordHash,
              createdAt: "2025-07-03T05:43:00.2526607Z",
            },
          }),
        1000
      )
    );

  const animate = (res) => {
    setAnimationStep(0);
    setApiData({
      url: API_URL,
      headers: API_HEADERS,
      payload: form,
      response: res,
    });
    setTimeout(() => {
      setAnimationStep(1);
      setTimeout(() => {
        setAnimationStep(2);
      }, 900);
    }, 900);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnimationStep(-1);
    setApiData(null);
    const res = await fakeCreateContact(form);
    animate(res);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "isActive") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "password") {
      setForm((prev) => ({
        ...prev,
        password: { passwordHash: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // cURL command generator
  const curl = apiData
    ? `curl -X POST ${apiData.url} \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(apiData.payload, null, 2)}'`
    : "";

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopyMsg("Copied!");
    setTimeout(() => setCopyMsg(""), 1200);
  };

  return (
    <>
      <MarkdownComponents markdown={markdown} />

      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: 24,
          background: "#f7f9fa",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 2px 8px #0001",
        }}
      >
        <h3 style={{ margin: "0 0 16px 0" }}>API Request Preview</h3>
        <div>
          <strong>URL:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {API_URL}
          </pre>
        </div>
        <div>
          <strong>Headers:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {JSON.stringify(API_HEADERS, null, 2)}
          </pre>
        </div>
        <div>
          <strong>Payload:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {JSON.stringify(form, null, 2)}
          </pre>
        </div>
        <div>
          <strong>cURL Command:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {curl}
          </pre>
          <button
            type="button"
            onClick={() => handleCopy(curl)}
            style={{
              background: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              cursor: "pointer",
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            Copy cURL
          </button>
          <span style={{ color: "#007bff", marginLeft: 8 }}>{copyMsg}</span>
        </div>

        {/* Animation Section */}
        {animationStep >= 0 && apiData && (
          <div
            style={{
              marginTop: 32,
              background: "#fff",
              padding: 24,
              borderRadius: 8,
              boxShadow: "0 2px 8px #0001",
            }}
          >
            <h3>Contact Creation Progress</h3>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {ANIMATION_STEPS.map((step, idx) => (
                <li
                  key={step.key}
                  style={{
                    opacity: animationStep >= idx ? 1 : 0.3,
                    fontWeight: animationStep === idx ? "bold" : "normal",
                    transition: "opacity 0.4s",
                    marginBottom: 10,
                  }}
                >
                  {step.label}
                  {step.key === "response" && animationStep >= idx && (
                    <div style={{ fontSize: 13, color: "#444", marginTop: 6 }}>
                      <strong>Response:</strong>
                      <pre style={{ background: "#f4f4f4", padding: 8 }}>
                        {JSON.stringify(apiData.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  {step.key === "done" && animationStep >= idx && (
                    <div style={{ marginTop: 8, color: "#28a745" }}>
                      <strong>🎉 Contact Created Successfully!</strong>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Minimal form fields */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label>
              Name:
              <input
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label>
              Email:
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label>
              Mobile:
              <input
                name="mobilePhone"
                required
                value={form.mobilePhone}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Organisation ID:
              <input
                name="contactOrganisationId"
                required
                value={form.contactOrganisationId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
                type="number"
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Division ID:
              <input
                name="divisionId"
                required
                value={form.divisionId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
                type="number"
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Department ID:
              <input
                name="departmentId"
                required
                value={form.departmentId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
                type="number"
              />
            </label>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Job Title:
              <input
                name="jobTitle"
                required
                value={form.jobTitle}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Known As:
              <input
                name="knownAs"
                required
                value={form.knownAs}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Office Location:
              <input
                name="officeLocation"
                required
                value={form.officeLocation}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <label>
            Password:
            <input
              type="password"
              name="password"
              required
              value={form.password.passwordHash}
              onChange={handleChange}
              style={{ width: "100%", marginTop: 4, padding: 8 }}
            />
          </label>
        </div>
        <div style={{ marginTop: 8 }}>
          <label>
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              style={{ marginRight: 8 }}
            />
            Is Active
          </label>
        </div>
        <button
          type="submit"
          style={{
            marginTop: 16,
            width: "100%",
            padding: 10,
            background: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          Create Contact
        </button>
      </form>
    </>
  );
}
